import { useEffect, useRef } from 'react';
import classNames from './NotificationsList.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { getNotificationText, NormalizeNotification } from '@/context/NotificationsContext';
import SVG from 'react-inlinesvg';
import trashIcon from '@/assets/svg/trash.svg';
import { NotificationStatus } from '@/types/services/notification';
import DOMPurify from 'dompurify';

const VIEW_THRESHOLD_MS = 2000;

interface NotificationRowProps {
  notification: NormalizeNotification;
  onMarkAsSeen: (id: string) => void;
  onDeleted: (id: string) => void;
}

const NotificationRow = ({ notification, onMarkAsSeen, onDeleted }: NotificationRowProps) => {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const { body, sendData, actionText } = getNotificationText(notification, t);
  const { notificationId, status } = notification;

  useEffect(() => {
    if (status !== NotificationStatus.Unread) {
      return;
    }

    let timer: any;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => {
            onMarkAsSeen(notificationId);
          }, VIEW_THRESHOLD_MS);
        } else {
          if (timer) {
            clearTimeout(timer);
          }
        }
      },
      {
        threshold: 0.7,
      }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => {
      observer.disconnect();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [notificationId, status, onMarkAsSeen]);

  return (
    <div ref={rowRef} className={classNames.notification}>
      <div
        className={classes(classNames.notificationData, {
          [classNames.unreadNotificationData]: status === NotificationStatus.Unread,
        })}
      >
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }} />
        <button
          onClick={() => {
            onDeleted(notificationId);
          }}
        >
          <SVG src={trashIcon} />
        </button>
      </div>
      {actionText && (
        <div
          className={classNames.notificationActionText}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(actionText) }}
        />
      )}
      <div className={classNames.notificationSendData}>{sendData}</div>
    </div>
  );
};

export default NotificationRow;
